import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FetchNestedCategories, FetchProductsBySubCategory } from "../Data";
import SubCategoryFiltersSidebar from "./SubCategoryFiltersSidebar";
import MobileFiltersDrawer from "./MobileFiltersDrawer";
import ProductGridWithSort from "./ProductGridWithSort";

/**
 * Amazon-style SubCategory Page
 * 2-column layout: Filters sidebar (left) + Product grid with sorting (right)
 * Mobile: Filters in drawer, products in single column
 * Includes brand filtering and customer review filters
 */
const SubCategoryPage = () => {
  const { subCategorySlug } = useParams();
  const navigate = useNavigate();
  
  const [subCategory, setSubCategory] = useState(null);
  const [parentCategory, setParentCategory] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    selectedBrands: [],
    priceMin: null,
    priceMax: null,
    customPriceMin: null,
    customPriceMax: null,
    inStockOnly: false,
    minDiscount: null,
    minRating: null,
  });

  // Load subcategory data and products
  useEffect(() => {
    const loadSubCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const categories = await FetchNestedCategories();
        
        // Find the subcategory and its parent category
        let foundSubCategory = null;
        let foundParentCategory = null;
        
        for (const category of categories || []) {
          if (category.subCategories) {
            const subCat = category.subCategories.find(sub => 
              sub.slug === subCategorySlug || 
              sub.subCategoryId === subCategorySlug ||
              sub.categoryId === subCategorySlug ||
              sub.name?.toLowerCase().replace(/\s+/g, '-') === subCategorySlug ||
              sub.categoryName?.toLowerCase().replace(/\s+/g, '-') === subCategorySlug
            );
            
            if (subCat) {
              foundSubCategory = subCat;
              foundParentCategory = category;
              break;
            }
          }
        }
        
        if (!foundSubCategory) {
          setError('Subcategory not found');
          return;
        }
        
        setSubCategory(foundSubCategory);
        setParentCategory(foundParentCategory);
        
        // Get subcategory ID
        const subCategoryId = foundSubCategory.subCategoryId;
        
        if (!subCategoryId) {
          console.error('No valid ID found in subcategory object:', foundSubCategory);
          setError('Invalid subcategory ID');
          return;
        }
        
        // Fetch all products for this subcategory
        const productsData = await FetchProductsBySubCategory(subCategoryId);
        setAllProducts(productsData || []);
        
      } catch (error) {
        console.error('Error loading subcategory:', error);
        setError('Failed to load subcategory data');
      } finally {
        setLoading(false);
      }
    };

    if (subCategorySlug) {
      loadSubCategoryData();
    }
  }, [subCategorySlug]);

  // Extract unique brands from products
  const availableBrands = useMemo(() => {
    const brands = [...new Set(allProducts.map(p => p.brand).filter(Boolean))];
    return brands.sort();
  }, [allProducts]);

  // Client-side filtering - Apply all filters to products
  const filteredProducts = useMemo(() => {
    let filtered = [...allProducts];

    // Filter by brand
    if (filters.selectedBrands && filters.selectedBrands.length > 0) {
      filtered = filtered.filter(product => 
        filters.selectedBrands.includes(product.brand)
      );
    }

    // Filter by price range
    const minPrice = filters.customPriceMin || filters.priceMin;
    const maxPrice = filters.customPriceMax || filters.priceMax;
    
    if (minPrice !== null && minPrice !== undefined) {
      filtered = filtered.filter(product => {
        const discount = (product.discount / 100) || 0;
        const price = product.price * (1 - discount);
        return price >= minPrice;
      });
    }
    
    if (maxPrice !== null && maxPrice !== undefined && maxPrice !== Infinity) {
      filtered = filtered.filter(product => {
        const discount = (product.discount / 100) || 0;
        const price = product.price * (1 - discount);
        return price <= maxPrice;
      });
    }

    // Filter by rating
    if (filters.minRating) {
      filtered = filtered.filter(product => 
        (product.rating || 4.5) >= filters.minRating
      );
    }

    // Filter by availability
    if (filters.inStockOnly) {
      filtered = filtered.filter(product => product.stockQuantity > 0);
    }

    // Filter by minimum discount
    if (filters.minDiscount) {
      filtered = filtered.filter(product => {
        const discount = (product.discount / 100) || 0;
        return discount >= filters.minDiscount;
      });
    }

    return filtered;
  }, [allProducts, filters]);

  // Active filters count for mobile button
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.selectedBrands?.length > 0) count++;
    if (filters.priceMin || filters.priceMax || filters.customPriceMin || filters.customPriceMax) count++;
    if (filters.inStockOnly) count++;
    if (filters.minDiscount) count++;
    if (filters.minRating) count++;
    return count;
  }, [filters]);

  // Loading state
  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-semibold">Loading products...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <p className="text-red-600 font-semibold mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')} 
            className="bg-orange-500 text-white px-6 py-2 rounded hover:bg-orange-600 mr-2"
          >
            Go Home
          </button>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Breadcrumb - Amazon style */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="container mx-auto px-4">
          <nav className="flex text-sm text-gray-600">
            <Link to="/" className="hover:text-orange-600 hover:underline">Home</Link>
            <span className="mx-2">&rsaquo;</span>
            {parentCategory && (
              <>
                <Link 
                  to={`/category/${parentCategory.slug || parentCategory.categoryId}`}
                  className="hover:text-orange-600 hover:underline"
                >
                  {parentCategory.name}
                </Link>
                <span className="mx-2">&rsaquo;</span>
              </>
            )}
            <span className="text-gray-900">{subCategory?.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Container: 2-column layout (sidebar + content) */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex gap-6">
          
          {/* LEFT COLUMN: Filters Sidebar - Desktop only, sticky */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <SubCategoryFiltersSidebar
              filters={filters}
              onFilterChange={setFilters}
              availableBrands={availableBrands}
              parentCategory={parentCategory}
              subCategory={subCategory}
            />
          </aside>

          {/* RIGHT COLUMN: Product Results */}
          <main className="flex-1 min-w-0">
            {/* Mobile: Filter Button + Results Count */}
            <div className="lg:hidden flex items-center justify-between mb-4 bg-white border border-gray-200 rounded-sm p-3">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{filteredProducts.length}</span> results
              </div>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="text-sm font-medium">Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            {/* SubCategory Header */}
            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-normal text-gray-900 mb-2">
                {subCategory?.name}
              </h1>
              {subCategory?.description && (
                <p className="text-sm text-gray-600">{subCategory.description}</p>
              )}
            </div>

            {/* Product Grid with Sorting and Pagination */}
            <ProductGridWithSort 
              products={filteredProducts} 
              resultsText={subCategory?.name}
            />
          </main>
        </div>
      </div>

      {/* Mobile Filters Drawer - Reuse MobileFiltersDrawer with custom content */}
      {mobileFiltersOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={() => setMobileFiltersOpen(false)}
          />

          {/* Drawer */}
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl shadow-2xl max-h-[85vh] overflow-y-auto transform transition-transform duration-300 ease-out md:inset-y-0 md:left-auto md:right-0 md:w-96 md:rounded-none md:rounded-l-2xl">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-gray-900">Filters</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close filters"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Swipe indicator (mobile only) */}
            <div className="md:hidden flex justify-center pt-2 pb-1">
              <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
            </div>

            {/* Filters Content */}
            <div className="p-4">
              <SubCategoryFiltersSidebar
                filters={filters}
                onFilterChange={setFilters}
                availableBrands={availableBrands}
                parentCategory={parentCategory}
                subCategory={subCategory}
              />
            </div>

            {/* Footer with Apply button */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3">
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubCategoryPage;
